package com.snakesworld.game.ui

import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.PointerInputScope
import androidx.compose.ui.input.pointer.PointerType
import androidx.compose.ui.unit.dp
import com.snakesworld.game.domain.ArenaSwipe
import com.snakesworld.game.engine.Direction

/**
 * Detects a single-finger arena swipe / flick and reports one cardinal [Direction]
 * when the motion commits (or on lift for a short, fast flick).
 *
 * Mouse and scroll pointers are ignored so desktop/web drag, wheel zoom, and
 * keyboard-focus tap handlers keep working. Two-finger contact aborts so
 * pinch-to-zoom can proceed. HUD chrome sits above the canvas and therefore
 * continues to own its own touches.
 */
internal suspend fun PointerInputScope.detectArenaSwipe(
    onSwipe: (Direction) -> Unit,
) {
    val minDistancePx = 36.dp.toPx()
    val minFlickDistancePx = 16.dp.toPx()

    awaitEachGesture {
        val down = awaitFirstDown(requireUnconsumed = false)
        if (!down.type.isArenaSwipePointer()) return@awaitEachGesture

        val pointerId = down.id
        val startTimeMs = down.uptimeMillis
        var totalDx = 0f
        var totalDy = 0f
        var committed = false

        while (true) {
            val event = awaitPointerEvent()
            if (event.type == PointerEventType.Scroll) continue

            // Second finger = pinch zoom; do not consume so transform can run.
            if (event.changes.any { it.id != pointerId && it.pressed }) break

            val change = event.changes.firstOrNull { it.id == pointerId } ?: break
            // Use raw position (not positionChange) so a consumed transform
            // gesture still yields a usable swipe delta.
            totalDx += change.position.x - change.previousPosition.x
            totalDy += change.position.y - change.previousPosition.y

            if (!committed) {
                val dir = ArenaSwipe.committedDirection(totalDx, totalDy, minDistancePx)
                if (dir != null) {
                    committed = true
                    onSwipe(dir)
                    change.consume()
                }
            } else {
                change.consume()
            }

            if (!change.pressed) {
                if (!committed) {
                    val elapsedSeconds = (change.uptimeMillis - startTimeMs) / 1000f
                    val flick = ArenaSwipe.flickDirection(
                        dx = totalDx,
                        dy = totalDy,
                        elapsedSeconds = elapsedSeconds,
                        minDistancePx = minFlickDistancePx,
                    )
                    if (flick != null) onSwipe(flick)
                }
                break
            }
        }
    }
}

private fun PointerType.isArenaSwipePointer(): Boolean =
    this == PointerType.Touch || this == PointerType.Stylus
